import {
  type ReactNode,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Button, Checkbox, Label, Popover, cn } from "@heroui/react";
import Icon from "@mdi/react";
import { mdiFilterOutline } from "@mdi/js";

const ABSOLUTE_MAX_HEIGHT = 420;
const MIN_HEIGHT = 100;
const VIEWPORT_MARGIN = 16;

export type FilterGroupDef = {
  id: string;
  label: string;
  options: { id: string; label: string }[];
  value: string[];
  onChange: (val: string[]) => void;
  /** Если true — поведение как у radio: выбор одного снимает остальные */
  singleSelect?: boolean;
};

export function CombinedFilter({
  groups,
  extra,
  testId = "combined-filter",
}: {
  groups: FilterGroupDef[];
  extra?: ReactNode;
  testId?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const contentDivRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(ABSOLUTE_MAX_HEIGHT);

  const recomputeHeight = useCallback(() => {
    const el = contentDivRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const available = window.innerHeight - top - VIEWPORT_MARGIN;
    setMaxHeight(
      Math.min(ABSOLUTE_MAX_HEIGHT, Math.max(MIN_HEIGHT, available)),
    );
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setMaxHeight(ABSOLUTE_MAX_HEIGHT);
      return;
    }
    const raf = requestAnimationFrame(recomputeHeight);
    window.addEventListener("scroll", recomputeHeight, { passive: true });
    window.addEventListener("resize", recomputeHeight, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", recomputeHeight);
      window.removeEventListener("resize", recomputeHeight);
    };
  }, [isOpen, recomputeHeight]);

  const totalActive = groups.reduce((acc, g) => acc + g.value.length, 0);
  const isActive = totalActive > 0;

  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button
        variant="outline"
        size="lg"
        isIconOnly
        aria-label="Фильтры"
        className="scd-multi-filter-btn"
        data-testid={`${testId}-button`}
      >
        <Icon path={mdiFilterOutline} size={0.8} />
        {isActive && (
          <span className="scd-multi-filter-btn__badge">{totalActive}</span>
        )}
      </Button>
      <Popover.Content
        placement="bottom start"
        shouldFlip={false}
        className="scd-multi-filter-popover"
      >
        <Popover.Dialog>
          <div
            ref={contentDivRef}
            className="scd-combined-filter"
            style={{ maxHeight }}
            data-testid={`${testId}-panel`}
          >
            {groups.map((group, idx) => (
              <div
                key={group.id}
                className={cn(
                  "scd-combined-filter__group",
                  idx > 0 && "scd-combined-filter__group--sep",
                )}
              >
                <div className="scd-combined-filter__group-label">
                  {group.label}
                </div>
                <div className="scd-single-filter-list">
                  {group.options.map((opt) => {
                    const isSelected = group.value.includes(opt.id);
                    return (
                      <Checkbox
                        key={opt.id}
                        id={`${group.id}-${opt.id}`}
                        isSelected={isSelected}
                        onChange={(checked) => {
                          if (group.singleSelect) {
                            // radio-mode: выбор одного снимает остальные
                            group.onChange(checked ? [opt.id] : []);
                          } else if (checked) {
                            group.onChange([...group.value, opt.id]);
                          } else {
                            group.onChange(
                              group.value.filter((v) => v !== opt.id),
                            );
                          }
                        }}
                        className="scd-filter-checkbox"
                      >
                        <Checkbox.Control className="scd-filter-checkbox__control">
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        <Checkbox.Content>
                          <Label
                            htmlFor={`${group.id}-${opt.id}`}
                            className="scd-filter-checkbox__label"
                          >
                            {opt.label}
                          </Label>
                        </Checkbox.Content>
                      </Checkbox>
                    );
                  })}
                </div>
              </div>
            ))}
            {extra && (
              <div className="scd-combined-filter__group scd-combined-filter__group--sep">
                {extra}
              </div>
            )}
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}
